<template lang="pug">
yz-dialog(v-model="showDialog" fullscreen persistent fluid scrollable max-width="1000")
  yz-card
    yz-dialog-fullscreen-header(:title="dialogTitle" @back="onCloseDialog" @cancel="onCloseDialog" @save="onSaveData" :loading="isLoading")
    yz-dialog-fullscreen-content
      add-new-medicine-dialog-skeleton(v-if="isFetchingMedication")
      div(v-else-if="isFetchError")
        yz-empty-state-card(icon="mdi-alert-circle-outline" icon-color="yz-danger-500" :title="$t('error_message.unable_to_load_data')" :description="$t('error_message.please_try_again')" :action-label="$t('retry')" @action-click="initData")
      v-form(v-else ref="form" v-model="isFormValid")
        div(style="max-width: 1100px; margin: 0 auto;")
          //- Inventory details
          yz-card.mb-4(outlined)
            .pa-6
              .yz-heading-8.text-neutral-700.mb-3 Inventory details
              v-row(no-gutters)
                v-col.pr-3(cols="6")
                  .yz-heading-9.text-neutral-700.mb-1 {{ $t('name') }}
                  yz-text-field(v-model="medicine.name" :placeholder="$t('name')" required)
                v-col.pl-3(cols="6")
                  .yz-heading-9.text-neutral-700.mb-1 Generic name
                  yz-text-field(v-model="medicine.generic_name" placeholder="Generic name")
              v-row.mt-3(no-gutters)
                v-col.pr-3(cols="4")
                  .yz-heading-9.text-neutral-700.mb-1 Category
                  yz-dropdown(v-model="medicine.category" placeholder="Category" :items="categoryOptions" item-text="text" item-value="value" :rules="rules.not_empty_select")
                v-col.px-3(cols="4")
                  .yz-heading-9.text-neutral-700.mb-1 Group
                  medication-group-dropdown(v-model="medicine.groups" :error="false" size="lg" distinct, :team-hq-id="company.id")
                v-col.pl-3(cols="4")
                  .yz-heading-9.text-neutral-700.mb-1 SKU
                  medication-sku-dropdown(v-model="medicine.sku" :error="false" size="lg" distinct :team-hq-id="company.id")

          //- Dosage and instructions
          yz-card.mb-4(outlined)
            .pa-6
              div.d-flex.justify-space-between
                div
                  .yz-heading-8.text-neutral-700 Dosage and instructions
                  .yz-regular-body-sm.mt-2 Set default value for dosage and instructions. This will be a suggestion value to providers. They can change it accordingly while dispensing the medication to patients. Disable if your item does not need one.
                div
                  yz-switch(v-model="instructionEnabled" size="md")
              v-row.mt-3(v-if="instructionEnabled" no-gutters)
                v-col.pr-3(cols="4")
                  .yz-heading-9.text-neutral-700.mb-1 Dosage amount
                  yz-text-field(v-model="medicine.dosage_value" placeholder="Dosage amount" type="number")
                v-col.px-3(cols="4")
                  .yz-heading-9.text-neutral-700.mb-1 Dosage unit
                  medication-dosage-unit-dropdown(v-model="medicine.dosage_unit" :error="false" size="lg" distinct :team-hq-id="company.id")
                v-col.pl-3(cols="4")
                  .yz-heading-9.text-neutral-700.mb-1 Instruction
                  medication-instruction-dropdown(v-model="medicine.instruction" :error="false" size="lg" distinct :team-hq-id="company.id")
              v-row.mt-3(v-if="instructionEnabled" no-gutters)
                v-col.pr-3(cols="4")
                  .yz-heading-9.text-neutral-700.mb-1 Precaution
                  medication-precaution-dropdown(v-model="medicine.precaution" :error="false" size="lg" distinct :team-hq-id="company.id")
                v-col.px-3(cols="4")
                  .yz-heading-9.text-neutral-700.mb-1 Frequency
                  medication-frequency-dropdown(v-model="medicine.frequency" :error="false" size="lg" distinct :team-hq-id="company.id")
                v-col.pl-3(cols="4")
                  .yz-heading-9.text-neutral-700.mb-1 Duration
                  medication-duration-dropdown(v-model="medicine.duration" :error="false" size="lg" distinct :team-hq-id="company.id")
              v-row.mt-3(v-if="instructionEnabled" no-gutters)
                v-col.pr-3(cols="4")
                  .yz-heading-9.text-neutral-700.mb-1 Indication
                  medication-indication-dropdown(v-model="medicine.indication" :error="false" size="lg" distinct :team-hq-id="company.id")
                v-col.pl-3(cols="4")
                  .yz-heading-9.text-neutral-700.mb-1 Dispense quantity
                  yz-text-field(v-model="medicine.default_qty" placeholder="Default usage amount" type="number")

          //- Unit of measurement conversion
          yz-card.mb-4(outlined)
            .pa-6
              div.d-flex.justify-space-between
                div
                  .yz-heading-8.text-neutral-700 Unit of measurement conversion
                  .yz-regular-body-sm.mt-2 Set how this product’s stock unit converts into its dispense unit. Enable this only if the stock UOM is different from how the item is dispensed or sold. The system will calculate quantities based on the conversion you set.
                div
                  yz-switch(v-model="uomConversionEnabled" size="md")
              div.mt-4(v-if="uomConversionEnabled")
                //- Summary table when conversions exist
                div(v-if="uomConversions.length > 0")
                  yz-simple-table
                    template(v-slot:default)
                      thead
                        tr
                          th.yz-medium-body-xs.text-neutral-700 UOM
                          th.yz-medium-body-xs.text-neutral-700 Convert to
                          th.yz-medium-body-xs.text-neutral-700 Multiply by
                          th.yz-medium-body-xs.text-neutral-700 Summary
                      tbody
                        tr(v-for="(conv, index) in uomConversions" :key="index")
                          td.py-3
                            .yz-regular-body-sm.text-neutral-700 {{ conv.uom }}
                          td.py-3
                            .yz-regular-body-sm.text-neutral-700 {{ conv.convert_to }}
                          td.py-3
                            .yz-regular-body-sm.text-neutral-700 {{ conv.multiplier }}
                          td.py-3
                            .yz-regular-body-sm 1 {{ conv.uom }} = {{ conv.multiplier }} {{ conv.convert_to }}
                  .mt-3
                    yz-btn(label="Edit conversions" size="sm" outlined @click="onAddUomConversion")
                //- Empty state when no conversions
                yz-empty-state-card(v-else icon="$yzi-calculator-line" title="No converted UOMs" description="Any converted UOMs will appear in here" action-label="Add UOM conversion" @action-click="onAddUomConversion")

          //- Pricing
          yz-card.mb-4(outlined)
            .pa-6
              .yz-heading-8.text-neutral-700 Pricing
              div.d-flex.align-center.mt-2
                .yz-regular-body-sm Set default pricing tier for this item. You can manage your pricing tier in settings.
                yz-btn(label="Go to pricing tier setting" underline append-icon="$yzi-arrow-up-right" @click="$router.push('/admin/hq/inventory/settings/price-tier')")
              .yz-heading-9.text-neutral-700.mt-6 Set default pricing
              .yz-regular-body-sm.mt-1 This pricing will be shared across all branches.
              yz-simple-table.mt-3
                template(v-slot:default)
                  thead
                    tr
                      th.yz-medium-body-xs.text-neutral-700 Price tier
                      th.yz-medium-body-xs.text-neutral-700 Payment method
                      th.yz-medium-body-xs.text-neutral-700(style="width: 350px;") Default price
                  tbody
                    template(v-for="tier in pricingTiers")
                      tr(:key="tier.id")
                        td.py-3
                          .yz-heading-9.text-neutral-700 {{ tier.name }}
                          .yz-regular-body-sm {{ tier.description }}
                        td.py-3
                          .d-flex.align-center.gap-2
                            div
                              .yz-regular-body-xs.text-neutral-700 Panel: {{ tier.panelText }}
                              .yz-regular-body-xs.text-neutral-700.mt-1 Self-Pay: {{ tier.selfPayText }}
                            yz-icon.text-neutral-500.cursor-pointer(v-if="tier.panelText !== 'None included'" size="16" @click="onOpenTierDetails(tier)") mdi-information-outline
                        td.py-3
                          template(v-if="!hasActiveUomConversions")
                            yz-text-field(v-model="tier.price" placeholder="0" :prefix="$currency.symbol" type="number" size="lg" :rules="rules.cost")
                          template(v-else)
                            .d-flex.align-center.justify-space-between.cursor-pointer(@click="tier.expanded = !tier.expanded")
                              .yz-regular-body-sm.text-neutral-500.font-italic Multiple UOMs
                              yz-icon(size="16") {{ tier.expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                      template(v-if="hasActiveUomConversions && tier.expanded")
                        tr(v-for="(uomPrice, uIdx) in tier.price_per_uom" :key="tier.id + '-uom-' + uIdx")
                          td
                          td
                          td.py-2
                            .d-flex.align-center.gap-2
                              .yz-medium-body-sm.text-neutral-700(style="min-width: 80px;") {{ uomPrice.uom }}
                              yz-text-field(v-model="uomPrice.price" placeholder="0" :prefix="$currency.symbol" type="number" size="lg" background-color="neutral200" :rules="rules.cost")
              yz-divider.my-5
              .d-flex.align-start.justify-space-between
                div
                  .yz-heading-9.text-neutral-700 Add default pricing override
                  .yz-regular-body-sm.mt-1 This pricing will override the default pricing and adjusted according to your selected branches and pricing setup.
                yz-btn(v-if="priceOverrides.length === 0" label="Add price override" size="sm" @click="showOverridePricingDialog = true")

              //- Saved overrides list
              template(v-if="priceOverrides.length > 0")
                .d-flex.align-center.justify-end.mt-4.mb-2
                  yz-btn(label="Add price override" size="sm" @click="showOverridePricingDialog = true")
                yz-simple-table
                  template(v-slot:default)
                    thead
                      tr
                        th.yz-medium-body-xs.text-neutral-700(style="width: 250px;") Branches
                        th.yz-medium-body-xs.text-neutral-700(style="width: 250px;") Price tier
                        th.yz-medium-body-xs.text-neutral-700(style="width: 250px;") Payment method
                        th.yz-medium-body-xs.text-neutral-700(style="width: 250px;") Override price
                        th
                    tbody
                      template(v-for="(override, oIdx) in priceOverrides")
                        template(v-for="branchId in override.branches")
                          //- Branch row (expandable)
                          tr.cursor-pointer(:key="'ob-' + oIdx + '-' + branchId" :class="{ 'override-branch-expanded': isOverrideBranchExpanded(oIdx, branchId) }" @click="onToggleOverrideBranch(oIdx, branchId)")
                            td.py-3
                              .d-flex.align-center.gap-2
                                .yz-heading-9.text-neutral-700 {{ getBranchName(branchId) }}
                                yz-icon(size="16") {{ isOverrideBranchExpanded(oIdx, branchId) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                            td
                            td
                            td
                            td.py-3
                              yz-btn(v-if="branchId === override.branches[0]" label="Edit" size="sm" outlined @click.stop="onEditOverride(oIdx)")
                          //- Tier sub-rows when branch expanded
                          template(v-if="isOverrideBranchExpanded(oIdx, branchId)")
                            template(v-for="(tierData, tIdx) in override.priceTiers")
                              tr(:key="'ot-' + oIdx + '-' + branchId + '-' + tIdx")
                                td
                                td.py-3
                                  .yz-heading-9.text-neutral-700 {{ getOverrideTierName(tierData) }}
                                  .yz-regular-body-sm {{ getOverrideTierDescription(tierData) }}
                                td.py-3
                                  .d-flex.align-center.gap-2
                                    div
                                      .yz-regular-body-xs.text-neutral-700 Panel: {{ getOverrideTierPanelText(tierData) }}
                                      .yz-regular-body-xs.text-neutral-700.mt-1 Self-Pay: {{ getOverrideTierSelfPayText(tierData) }}
                                    yz-icon.text-neutral-500.cursor-pointer(v-if="getOverrideTierPanelText(tierData) !== 'None included'" size="16" @click.stop="onOpenTierDetails(getTierById(tierData.price_tier))") mdi-information-outline
                                td.py-3
                                  template(v-if="hasActiveUomConversions")
                                    .d-flex.align-center.justify-space-between.cursor-pointer(@click.stop="onToggleOverrideUom(oIdx, branchId, tIdx)")
                                      .yz-regular-body-sm.text-neutral-500.font-italic Multiple UOMs
                                      yz-icon(size="16") {{ isOverrideUomExpanded(oIdx, branchId, tIdx) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                                  template(v-else)
                                    .d-flex.align-center.justify-end.gap-1
                                      .yz-regular-body-sm.text-neutral-700(v-if="tierData.price != null && tierData.price !== ''") {{ tierData.price * $currency.multiplier | displayPrice }}
                                      .yz-regular-body-sm.text-neutral-700(v-else) —
                                      yz-tooltip(v-if="tierData.updated_by_branch" direction="top" max-width="250")
                                        yz-icon.cursor-pointer.text-neutral-500(size="16") mdi-information-outline
                                        template(#tooltip)
                                          .yz-heading-9.white--text Price updated by branch and is reflected in HQ records.
                                td
                              //- UOM sub-rows
                              template(v-if="hasActiveUomConversions && isOverrideUomExpanded(oIdx, branchId, tIdx)")
                                tr(v-for="(uomPrice, uIdx) in tierData.price_per_uom" :key="'ou-' + oIdx + '-' + branchId + '-' + tIdx + '-' + uIdx")
                                  td
                                  td
                                  td
                                  td.py-2
                                    .d-flex.align-center.justify-space-between
                                      .yz-medium-body-sm.text-neutral-700(style="min-width: 80px;") {{ uomPrice.uom }}
                                      .d-flex.align-center.gap-1
                                        .yz-regular-body-sm.text-neutral-700(v-if="uomPrice.price != null && uomPrice.price !== ''") {{ uomPrice.price * $currency.multiplier | displayPrice }}
                                        .yz-regular-body-sm.text-neutral-700(v-else) —
                                        yz-tooltip(v-if="uomPrice.updated_by_branch" direction="top" max-width="250")
                                          yz-icon.cursor-pointer.text-neutral-500(size="16") mdi-information-outline
                                          template(#tooltip)
                                            .yz-heading-9.white--text Price updated by branch and is reflected in HQ records.
                                  td

          //- Stock details
          yz-card.mb-4(outlined)
            .pa-6
              div.d-flex.align-center.justify-space-between
                .yz-heading-8.text-neutral-700 Stock details
                yz-btn(v-if="isEditMode" label="View stock history" size="sm" @click="onViewStockHistory")
              v-row.mt-3(no-gutters align="start")
                v-col.pr-3(cols="4")
                  div.d-flex(style="height: 48px;")
                    div.button-box(style="min-width: 150px;")
                      div.d-flex.align-center.fill-height
                        .yz-medium-body-sm.text-neutral-700.mx-3.text-no-wrap Total stock on hand
                    v-text-field.text-sm(:value="stockSummaryDisplay.totalOnHand" placeholder="0" background-color="neutral300" disabled outlined dense height="48" hide-details, style="border-radius: 0 10px 10px 0;")
                      template(v-slot:append)
                        yz-tooltip(v-if="uomConversionEnabled && stockUomEquivalents.totalOnHand.length > 0" direction="top" max-width="250")
                          yz-icon.mt-2.cursor-pointer(size="18") mdi-information-outline
                          template(#tooltip)
                            .yz-heading-9.white--text Equivalent to
                            ul.pl-4.mt-1
                              li.yz-regular-body-sm.white--text(v-for="(equiv, idx) in stockUomEquivalents.totalOnHand" :key="idx") {{ equiv }}
                v-col.px-3(cols="4")
                  div.d-flex(style="height: 48px;")
                    div.button-box(style="min-width: 150px;")
                      div.d-flex.align-center.fill-height
                        .yz-medium-body-sm.text-neutral-700.mx-3.text-no-wrap Total available
                    v-text-field.text-sm(:value="stockSummaryDisplay.totalAvailable" placeholder="0" background-color="neutral300" disabled outlined dense height="48" hide-details, style="border-radius: 0 10px 10px 0;")
                      template(v-slot:append)
                        yz-tooltip(v-if="uomConversionEnabled && stockUomEquivalents.totalAvailable.length > 0" direction="top" max-width="250")
                          yz-icon.mt-2.cursor-pointer(size="18") mdi-information-outline
                          template(#tooltip)
                            .yz-heading-9.white--text Equivalent to
                            ul.pl-4.mt-1
                              li.yz-regular-body-sm.white--text(v-for="(equiv, idx) in stockUomEquivalents.totalAvailable" :key="idx") {{ equiv }}
                v-col.pl-3(cols="4")
                  div.d-flex(style="height: 48px;")
                    div.button-box(style="min-width: 120px;")
                      div.d-flex.align-center.fill-height
                        .yz-medium-body-sm.text-neutral-700.mx-3.text-no-wrap Default UOM
                    medication-unit-of-measure-dropdown.flex-grow-1(v-model="medicine.uom" :error="false" distinct :team-hq-id="company.id" field-min-width="0" :field-style="defaultUomFieldStyle" :allowed-values="uomAllowedValues" :deletable="!uomConversionEnabled" :searchable="!uomConversionEnabled" @input="uomError = false")
                  .text-xs.danger500--text.mt-1(v-if="uomError" style="padding-left: 120px;") Default UOM must be one of the values from the UOM conversion list

              div(v-if="!isEditMode")
                .yz-heading-8.text-neutral-700.mt-6 Add your initial stock
                v-row.mt-3(no-gutters)
                  v-col.pr-3(cols="4")
                    .yz-heading-9.text-neutral-700.mb-1 Stock cost
                    yz-text-field(v-model="stock.cost" placeholder="0.00" :prefix="$currency.symbol" type="number" size="lg" :rules="rules.cost" @input="onUpdateCost")
                  v-col.px-3(cols="4")
                    .yz-heading-9.text-neutral-700.mb-1 Cost per item
                    yz-text-field(v-model="medicine.cost" placeholder="0.00" :prefix="$currency.symbol" type="number" disabled background-color="neutral200" size="lg")
                  v-col.pl-3(cols="4")
                    .yz-heading-9.text-neutral-700.mb-1 Batch number
                    yz-text-field(v-model="stock.batch_no" placeholder="Batch number" size="lg")

              v-row.mt-5(no-gutters)
                v-col.pr-3(cols="4")
                  .yz-heading-9.text-neutral-700.mb-1 Stock warning amount
                  yz-text-field(v-model="medicine.stock_amount_warning" placeholder="Stock amount" type="number" size="lg" :rules="rules.cost")
                v-col.px-3(cols="4")
                  .yz-heading-9.text-neutral-700.mb-1 Stock expiry warning
                  yz-text-field(v-model="medicine.stock_expiry_warning" placeholder="0" type="number" suffix="days" size="lg" :rules="rules.cost")
                v-col.pl-3(v-if="!isEditMode" cols="4")
                  .yz-heading-9.text-neutral-700.mb-1 Expiry date
                  yz-text-field(v-model="expiryDateField" placeholder="Expiry date" readonly clickable prepend-inner-icon="$yzi-calendar" clearable size="lg" @click="showExpiredPicker = true" @click:clear="selectedExpiryDate = null")
                  yz-dialog(v-model="showExpiredPicker" max-width="380px")
                    yz-card
                      yz-dialog-normal-content.px-2.pt-2.pb-0
                        yz-date-picker(v-model="selectedExpiryDate" scrollable @change="onPickDate")

              v-row.mt-5(v-if="isEditMode" no-gutters)
                v-col.pr-3(cols="4")
                  .yz-heading-9.text-neutral-700.mb-1 Latest cost price per item
                  yz-text-field(:value="medicine.last_price" placeholder="0.00" :prefix="$currency.symbol" :suffix="medicine.uom" type="number" disabled background-color="yz-neutral-300" size="lg")
                v-col.pl-3(cols="4")
                  .yz-heading-9.text-neutral-700.mb-1 Average cost price per item
                  yz-text-field(:value="medicine.cost" placeholder="0.00" :prefix="$currency.symbol" :suffix="medicine.uom" type="number" disabled background-color="yz-neutral-300" size="lg")

          //- Assign inventory to branches and warehouses
          yz-card.mb-4(outlined)
            .pa-6
              v-row.mt-4(no-gutters)
                v-col(cols="8")
                  .yz-heading-8.text-neutral-700 Assign inventory to branches and warehouses
                  .yz-regular-body-sm.mt-2 Assign this inventory to your branches and warehouses. Toggle on to enable stock tracking for each location.
                v-spacer
                v-col(cols="auto")
                  .d-flex.gap-1
                    hq-warehouse-dropdown(v-model="branchFilter.warehouse")
                    hq-branch-dropdown(v-model="branchFilter.branch")

              div.mt-3(style="border: 1px solid #D4D4D4; border-radius: 10px; overflow: hidden;")
                yz-data-table(:headers="locationHeaders" :items="paginatedLocations" item-key="id" disable-sort header-class="yz-medium-body-xs" body-class="yz-regular-body-sm" no-outer-border radius="0")
                  template(v-slot:item.name="{ item }")
                    .yz-heading-9.text-neutral-700 {{ item.name }}
                  template(v-slot:item.onHand="{ item }")
                    template(v-if="item.enabled")
                      yz-text-field(v-if="!isEditMode" v-model="item.inventoryQty" placeholder="0" type="number" size="sm" style="max-width: 80px; margin-left: auto;" :rules="rules.cost" @input="onUpdateCost")
                      template(v-else)
                        yz-tooltip(v-if="!originalBranchCompanyIds.includes(item.companyId)" text="You can add stock using the stock history action" no-arrow)
                          .text-neutral-700.cursor-help {{ item.onHand }}
                        .text-neutral-700(v-else) {{ item.onHand }}
                    .text-neutral-400(v-else) Not stocked
                  template(v-slot:item.available="{ item }")
                    .text-neutral-700(v-if="item.enabled") {{ item.available }}
                    .text-neutral-400(v-else) Not stocked
                  template(v-slot:item.committed="{ item }")
                    .text-neutral-700(v-if="item.enabled") {{ item.committed }}
                    .text-neutral-400(v-else) Not stocked
                  template(v-slot:item.reserved="{ item }")
                    .text-neutral-400(v-if="item.type === 'warehouse'") N/A
                    .text-neutral-700(v-else-if="item.enabled") {{ item.reserved }}
                    .text-neutral-400(v-else) Not stocked
                  template(v-slot:item.storage="{ item }")
                    template(v-if="item.enabled")
                      yz-dropdown(v-if="item.storages.length > 0" v-model="item.locationId" :items="item.storages" item-text="value" item-value="id" size="sm" placeholder="Select" clearable)
                      .yz-regular-body-sm.text-neutral-400.font-italic(v-else) No storages found
                    .text-neutral-400(v-else) Not stocked
                  template(v-slot:item.actions="{ item }")
                    yz-switch(v-model="item.enabled" size="sm" @change="onToggleLocation(item, $event)")

                .py-2.px-4(style="border-top: 1px solid #D4D4D4;")
                  yz-pagination(v-model="locationPage" :page-size.sync="locationPageSize" :count="filteredLocations.length" type="item" text="locations" change-page-size arrow)

              yz-data-table.mt-3(:headers="locationHeaders" :items="summaryItems" item-key="id" disable-sort header-background-color="transparent" body-background-color="yz-neutral-200" body-class="yz-medium-body-sm" body-height="44px" header-height="0" border-color="white" no-outer-border)
                template(v-slot:header.name="{ header }")
                template(v-slot:header.onHand="{ header }")
                template(v-slot:header.available="{ header }")
                template(v-slot:header.committed="{ header }")
                template(v-slot:header.reserved="{ header }")
                template(v-slot:header.storage="{ header }")

                template(v-slot:item.name="{ item }")
                  .yz-medium-body-xs.text-neutral-500 {{ item.name }}
                template(v-slot:item.onHand="{ item }")
                  .text-neutral-700 {{ item.onHand }}
                template(v-slot:item.available="{ item }")
                  .text-neutral-700 {{ item.available }}
                template(v-slot:item.committed="{ item }")
                  .text-neutral-700 {{ item.committed }}
                template(v-slot:item.reserved="{ item }")
                  .text-neutral-700 {{ item.reserved }}
                template(v-slot:item.storage="{ item }")
                template(v-slot:item.actions="{ item }")
                  yz-switch.hidden(v-model="item.enabled" size="sm")

          //- Supplier details
          yz-card.mb-4(outlined)
            .pa-6
              supplier-details-section(v-model="medicine.supplier" :company-id="companyId" @supplier-deleted="onSupplierDeleted")

          //- Remarks
          yz-card.mb-4(outlined)
            .pa-6
              .yz-heading-8.text-neutral-700 Remarks
              yz-textarea.mt-4(v-model="medicine.remarks" placeholder="Write your remarks here" outlined rows="5")

    //- UOM Conversion Dialog
    uom-conversion-dialog(v-model="showUomConversionDialog" :default-uom="medicine.uom" :conversions="uomConversions" :team-hq-id="company.id" :original-base-uom="originalUom" :current-stock-on-hand="stockSummary.totalOnHand" :company-slug="companySlug" :medication-id="medicationId" @save="onSaveUomConversions" @base-uom-change-requested="onBaseUomChangeRequested")

    //- Stock warning dialog
    stock-warning-dialog(v-model="showStockWarningDialog")

    //- Unassign location confirmation dialog
    unassign-location-dialog(v-model="showUnassignDialog" :location-name="pendingUnassignItem ? pendingUnassignItem.name : ''" @confirmed="onConfirmUnassign" @canceled="onCancelUnassign")

    //- Override pricing dialog
    override-pricing-dialog(v-if="showOverridePricingDialog" :open.sync="showOverridePricingDialog" :branches="editingOverride ? branchLocations : availableBranchesForOverride" :price-tiers="pricingTiers" :uom-conversions="uomConversions" :uom-conversion-enabled="uomConversionEnabled" :default-uom="medicine.uom" :currency="$currency" :existing-override="editingOverride" @save="onSaveOverride" @closed="onCloseOverrideDialog")

    //- Pricing tier details dialog
    pricing-tier-details-dialog(v-if="showPricingTierDetailsDialog" :open.sync="showPricingTierDetailsDialog" :tier="selectedTier" @closed="selectedTier = null")

    //- CMS OS V2 consent dialog
    cms-os-v2-consent-dialog(v-if="showCmsOsV2ConsentDialog" :open.sync="showCmsOsV2ConsentDialog" @consented="onCmsOsV2Consented")

    //- Package archive dialog
    package-archive-dialog(v-model="showPackageArchiveDialog" :packages="packagesToArchive" :loading="isArchivingPackages" @confirmed="onConfirmArchivePackages" @closed="onCloseArchiveDialog")
    base-uom-change-dialog(v-model="showBaseUomChangeDialog" :original-uom="originalUom" :new-uom="pendingBaseUomData ? pendingBaseUomData.newBaseUom : null" :current-stock-on-hand="stockSummary.totalOnHand" :company-slug="companySlug" :medication-id="medicationId" :uom-conversions="pendingBaseUomData ? pendingBaseUomData.conversions : []" @saved="onBaseUomSaved")
    enable-uom-conversion-dialog(v-model="showEnableUomConversionDialog" :previous-uom="enableUomConversionData ? enableUomConversionData.previousUom : null" :new-base-uom="enableUomConversionData ? enableUomConversionData.newBaseUom : null" :default-uom="medicine.uom" :multiplier="enableUomConversionData ? enableUomConversionData.multiplier : 1" :current-stock-on-hand="stockSummary.totalOnHand" :company-slug="companySlug" :medication-id="medicationId" :uom-conversions="uomConversions" :api-endpoint="`/clinics/${companySlug}/hq/medications/${medicationId}`" @confirmed="onEnableUomConversionConfirmed")

</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { buildUomGraph, getUomScale } from '@/utils'
import MedicationGroupDropdown from '@/components/clinic/inventory/MedicationGroupDropdown.vue'
import MedicationDosageUnitDropdown from '@/components/clinic/inventory/MedicationDosageUnitDropdown.vue'
import MedicationInstructionDropdown from '@/components/clinic/inventory/MedicationInstructionDropdown.vue'
import MedicationPrecautionDropdown from '@/components/clinic/inventory/MedicationPrecautionDropdown.vue'
import MedicationFrequencyDropdown from '@/components/clinic/inventory/MedicationFrequencyDropdown.vue'
import MedicationDurationDropdown from '@/components/clinic/inventory/MedicationDurationDropdown.vue'
import MedicationIndicationDropdown from '@/components/clinic/inventory/MedicationIndicationDropdown.vue'
import MedicationUnitOfMeasureDropdown from '@/components/clinic/inventory/MedicationUnitOfMeasureDropdown.vue'
import MedicationSkuDropdown from '@/components/clinic/inventory/MedicationSkuDropdown.vue'
import UomConversionDialog from './UomConversionDialog.vue'
import SupplierDetailsSection from './SupplierDetailsSection.vue'
import HqBranchDropdown from '@/components/hq/inventory/HqBranchDropdown.vue'
import HqWarehouseDropdown from '@/components/hq/inventory/HqWarehouseDropdown.vue'
import StockWarningDialog from './StockWarningDialog.vue'
import UnassignLocationDialog from './UnassignLocationDialog.vue'
import OverridePricingDialog from './OverridePricingDialog.vue'
import AddNewMedicineDialogSkeleton from './AddNewMedicineDialogSkeleton.vue'
import PricingTierDetailsDialog from '@/components/clinic/inventory/PricingTierDetailsDialog.vue'
import CmsOsV2ConsentDialog from '@/components/clinic/dialogs/CmsOsV2ConsentDialog.vue'
import PackageArchiveDialog from './PackageArchiveDialog.vue'
import BaseUomChangeDialog from './BaseUomChangeDialog.vue'
import EnableUomConversionDialog from './EnableUomConversionDialog.vue'

export default {
  components: {
    AddNewMedicineDialogSkeleton,
    MedicationGroupDropdown,
    MedicationDosageUnitDropdown,
    MedicationInstructionDropdown,
    MedicationPrecautionDropdown,
    MedicationFrequencyDropdown,
    MedicationDurationDropdown,
    MedicationIndicationDropdown,
    MedicationUnitOfMeasureDropdown,
    MedicationSkuDropdown,
    UomConversionDialog,
    SupplierDetailsSection,
    HqBranchDropdown,
    HqWarehouseDropdown,
    StockWarningDialog,
    UnassignLocationDialog,
    OverridePricingDialog,
    PricingTierDetailsDialog,
    CmsOsV2ConsentDialog,
    PackageArchiveDialog,
    BaseUomChangeDialog,
    EnableUomConversionDialog
  },
  props: {
    open: {
      type: Boolean,
      default: false
    },
    medicationId: {
      type: [Number, String],
      default: null
    },
    overrideCompanySlug: {
      type: String,
      default: null
    },
    overrideCompanyId: {
      type: [Number, String],
      default: null
    }
  },
  data() {
    return {
      isLoading: false,
      isFetchingMedication: false,
      isFetchError: false,
      isFormValid: false,
      originalBranchCompanyIds: [],
      instructionEnabled: false,
      uomConversionEnabled: false,
      uomError: false,
      showCmsOsV2ConsentDialog: false,
      originalUom: null,
      originalDefaultUom: null,
      originalUomConversionEnabled: false,
      originalUomConversions: [],
      showEnableUomConversionDialog: false,
      enableUomConversionData: null,
      productId: null,
      showPackageArchiveDialog: false,
      packagesToArchive: [],
      isCheckingPackages: false,
      isArchivingPackages: false,
      showBaseUomChangeDialog: false,
      pendingBaseUomData: null,
      archiveFlowSource: null,
      showStockWarningDialog: false,
      showUnassignDialog: false,
      pendingUnassignItem: null,
      showUomConversionDialog: false,
      uomConversions: [],
      showExpiredPicker: false,
      selectedExpiryDate: null,
      expiryDateField: null,
      medicine: {},
      stock: {},
      isFetchingBranches: false,
      categoryOptions: [
        { text: 'Medication', value: 'medication' },
        { text: 'Equipment', value: 'equipment' },
        { text: 'Consumable', value: 'consumable' }
      ],
      pricingTiers: [],
      showOverridePricingDialog: false,
      showPricingTierDetailsDialog: false,
      selectedTier: null,
      editingOverrideIndex: null,
      editingOverride: null,
      priceOverrides: [],
      expandedOverrideBranches: {},
      expandedOverrideUoms: {},
      totalPanelCount: 0,
      locationMeta: null,
      branchLocations: [],
      branchFilter: {
        warehouse: null,
        branch: null
      },
      locationPage: 1,
      locationPageSize: 10,
      locationHeaders: [
        { text: 'Branches & warehouses', value: 'name', sortable: false, width: '25%' },
        { text: 'On hand', value: 'onHand', sortable: false, align: 'end', width: '12%' },
        { text: 'Available', value: 'available', sortable: false, align: 'end', width: '12%' },
        { text: 'Committed', value: 'committed', sortable: false, align: 'end', width: '12%' },
        { text: 'Reserved', value: 'reserved', sortable: false, align: 'end', width: '12%' },
        { text: 'Storage', value: 'storage', sortable: false, align: 'end', width: '20%' },
        { text: '', value: 'actions', sortable: false, align: 'center', width: '6%' }
      ],
      rules: {
        not_empty: [v => (v != null && v.toString().trim().length) || 'This field is required'],
        not_empty_select: [v => !!v || 'This field is required'],
        cost: [v => (!v || Number(v) >= 0) || 'Cost must be positive']
      }
    }
  },
  computed: {
    ...mapGetters({ company: 'getCompany' }),
    isEditMode() {
      return !!this.medicationId
    },
    dialogTitle() {
      return this.isEditMode ? 'Edit inventory' : 'Add inventory'
    },
    showDialog: {
      get() {
        return this.open
      },
      set(newValue) {
        this.$emit('update:open', newValue)
      }
    },
    paginatedLocations() {
      const start = (this.locationPage - 1) * this.locationPageSize
      const end = start + this.locationPageSize
      return this.filteredLocations.slice(start, end)
    },
    locationTotals() {
      if (this.locationMeta) {
        return {
          onHand: this.locationMeta.inventory_qty || 0,
          available: this.locationMeta.available_qty || 0,
          committed: this.locationMeta.committed_qty || 0,
          reserved: this.locationMeta.reserved_qty || 0
        }
      }
      return this.branchLocations.reduce((totals, loc) => {
        totals.onHand += loc.onHand
        totals.available += loc.available
        totals.committed += loc.committed
        totals.reserved += loc.reserved
        return totals
      }, { onHand: 0, available: 0, committed: 0, reserved: 0 })
    },
    availableBranchesForOverride() {
      const usedBranches = new Set(this.priceOverrides.flatMap(o => o.branches))
      return this.branchLocations.filter(b => !usedBranches.has(b.companyId))
    },
    companySlug() {
      return this.overrideCompanySlug || this.company.slug
    },
    companyId() {
      return this.overrideCompanyId || this.company.id
    },
    stockSummaryDisplay() {
      // When the enable-conversion flow is pending, display in originalDefaultUom (the persisted UOM).
      const uom = this.needsEnableUomConversionFlow ? (this.originalDefaultUom || '') : (this.medicine.uom || '')
      const format = (v) => Number.isInteger(v) ? `${v}` : parseFloat(v.toFixed(2)).toString()
      const onHand = format(this.stockSummary.totalOnHand)
      const available = format(this.stockSummary.totalAvailable)
      return {
        totalOnHand: uom ? `${onHand} ${uom}` : onHand,
        totalAvailable: uom ? `${available} ${uom}` : available
      }
    },
    uomAllowedValues() {
      if (!this.uomConversionEnabled || this.uomConversions.length === 0) return null
      const uoms = new Set()
      this.uomConversions.forEach(c => { uoms.add(c.uom); uoms.add(c.convert_to) })
      return [...uoms]
    },
    // Triggered only in edit mode when UOM conversion is toggled ON (was OFF) and new base UOM != previously persisted default UOM.
    // previousUom is the persisted default UOM (originalDefaultUom) — used as the dialog label and for the trigger comparison.
    // The multiplier is computed from medicine.uom (the user's currently-selected UOM in the new chain) → newBaseUom,
    // since previousUom may not exist in the new conversion graph.
    needsEnableUomConversionFlow() {
      if (!this.isEditMode) return null
      if (this.originalUomConversionEnabled || !this.uomConversionEnabled) return null
      if (!this.uomConversions || this.uomConversions.length === 0) return null
      const newBaseUom = this.uomConversions[this.uomConversions.length - 1].convert_to
      const previousUom = this.originalDefaultUom
      if (!previousUom || !newBaseUom || previousUom === newBaseUom) return null
      // Multiplier from currently-selected UOM (medicine.uom) to new base UOM.
      // Case 1 (selected === base, or not in graph): multiplier = 1 (qty unchanged, label only).
      // Case 2 (selected is elsewhere in graph): multiplier = scale(selected) / scale(base).
      const currentSelectedUom = this.medicine.uom
      const graph = buildUomGraph(this.uomConversions)
      let multiplier = 1
      if (currentSelectedUom && currentSelectedUom !== newBaseUom && graph[currentSelectedUom] != null && graph[newBaseUom] != null) {
        const selectedScale = getUomScale(graph, currentSelectedUom)
        const baseScale = getUomScale(graph, newBaseUom)
        if (baseScale > 0) multiplier = selectedScale / baseScale
      }
      return { previousUom, newBaseUom, multiplier }
    },
    defaultUomFieldStyle() {
      const base = 'border-radius: 0 10px 10px 0 !important;'
      return this.uomError ? `${base} border-color: var(--v-danger500) !important;` : base
    },
    stockSummary() {
      if (this.isEditMode) {
        // While EnableUomConversion flow is pending (toggled ON locally, not yet saved),
        // inventory_qty is still stored in originalDefaultUom — skip the local scale math.
        if (this.needsEnableUomConversionFlow) {
          return { totalOnHand: this.locationTotals.onHand, totalAvailable: this.locationTotals.available }
        }
        const scale = this.defaultUomScale
        const totalOnHand = this.locationTotals.onHand / scale
        const totalAvailable = this.locationTotals.available / scale
        return { totalOnHand, totalAvailable }
      }
      const totalOnHand = this.branchLocations.filter(l => l.enabled).reduce((sum, l) => sum + (Number(l.inventoryQty) || 0), 0)
      return { totalOnHand, totalAvailable: totalOnHand }
    },
    defaultUomScale() {
      if (!this.uomConversionEnabled || this.uomConversions.length === 0) return 1
      const graph = buildUomGraph(this.uomConversions)
      return getUomScale(graph, this.medicine.uom)
    },
    stockUomEquivalents() {
      // Hide equivalents tooltip while EnableUomConversion flow is pending — the conversion
      // hasn't been persisted yet so any locally-computed equivalents would be misleading.
      if (this.needsEnableUomConversionFlow) {
        return { totalOnHand: [], totalAvailable: [] }
      }
      if (!this.uomConversionEnabled || this.uomConversions.length === 0) {
        return { totalOnHand: [], totalAvailable: [] }
      }
      const rawOnHand = this.isEditMode ? this.locationTotals.onHand : this.stockSummary.totalOnHand * this.defaultUomScale
      const rawAvailable = this.isEditMode ? this.locationTotals.available : this.stockSummary.totalAvailable * this.defaultUomScale
      const graph = buildUomGraph(this.uomConversions)
      const selectedUom = this.medicine.uom
      const formatValue = (v) => Number.isInteger(v) ? `${v}` : parseFloat(v.toFixed(2)).toString()
      const buildEquivalents = (rawBaseQty) => {
        if (!rawBaseQty) return []
        return Object.keys(graph)
          .filter(uom => uom !== selectedUom)
          .map(uom => ({ uom, scale: getUomScale(graph, uom), value: rawBaseQty / getUomScale(graph, uom) }))
          .filter(({ value }) => Number(value.toFixed(2)) > 0)
          .sort((a, b) => b.scale - a.scale)
          .map(({ uom, value }) => `${formatValue(value)} ${uom}`)
      }
      return {
        totalOnHand: buildEquivalents(rawOnHand),
        totalAvailable: buildEquivalents(rawAvailable)
      }
    },
    summaryItems() {
      return [{
        id: 'totals',
        name: 'Total stock across all locations',
        onHand: this.locationTotals.onHand,
        available: this.locationTotals.available,
        committed: this.locationTotals.committed,
        reserved: this.locationTotals.reserved
      }]
    },
    hasActiveUomConversions() {
      return this.uomConversionEnabled && this.uomConversions.length > 0
    },
    filteredLocations() {
      const { warehouse: wf, branch: bf } = this.branchFilter
      return this.branchLocations.filter(loc => {
        if (loc.type === 'warehouse' && wf !== null && !wf.some(w => w.id === loc.id)) return false
        if (loc.type === 'branch' && bf !== null && !bf.some(b => b.id === loc.id)) return false
        return true
      })
    }
  },
  watch: {
    uomConversions() {
      this.refreshTierUomPrices()
    }
  },
  async mounted() {
    await this.initData()
  },
  methods: {
    ...mapActions(['showMessage']),
    onViewStockHistory() {
      this.$router.push({ name: 'HQStockHistory', params: { id: this.medicationId } })
    },
    getPanelCount(panels) {
      const count = panels ? panels.length : 0
      if (count === 0) return 'None included'
      if (this.totalPanelCount > 0 && count === this.totalPanelCount) return 'All included'
      return count + ' included'
    },
    getSelfPayCount(methods) {
      const count = methods ? methods.length : 0
      if (count === 0) return 'None included'
      return count + ' included'
    },
    getUomList() {
      if (!this.uomConversionEnabled || this.uomConversions.length === 0) return []
      const uoms = []
      this.uomConversions.forEach(c => {
        if (!uoms.includes(c.uom)) uoms.push(c.uom)
        if (!uoms.includes(c.convert_to)) uoms.push(c.convert_to)
      })
      return uoms
    },
    mapPricingTiers(data) {
      const results = data?.results || data || []
      this.totalPanelCount = data?.total_panels || 0
      this.pricingTiers = results.map(tier => ({
        id: tier.id,
        name: tier.name,
        description: tier.description,
        panels: tier.panels || [],
        self_pay: tier.self_pay || [],
        panelText: this.getPanelCount(tier.panels),
        selfPayText: this.getSelfPayCount(tier.self_pay),
        price: null,
        price_per_uom: this.getUomList().map(uom => ({ uom, price: null })),
        expanded: false
      }))
    },
    refreshTierUomPrices() {
      const uomList = this.getUomList()
      this.pricingTiers.forEach(tier => {
        const existing = tier.price_per_uom || []
        tier.price_per_uom = uomList.map(uom => {
          const found = existing.find(p => p.uom === uom)
          return { uom, price: found ? found.price : null }
        })
      })
    },
    getBranchName(branchId) {
      const branch = this.branchLocations.find(b => b.companyId === branchId)
      return branch ? branch.name : branchId
    },
    tierHasPrice(tier) {
      if (!this.hasActiveUomConversions) return tier.price !== null && tier.price !== ''
      return tier.price_per_uom.some(p => p.price !== null && p.price !== '')
    },
    onToggleOverrideBranch(oIdx, branchId) {
      const key = `${oIdx}-${branchId}`
      this.$set(this.expandedOverrideBranches, key, !this.expandedOverrideBranches[key])
    },
    onToggleOverrideUom(oIdx, branchId, tIdx) {
      const key = `${oIdx}-${branchId}-${tIdx}`
      this.$set(this.expandedOverrideUoms, key, !this.expandedOverrideUoms[key])
    },
    isOverrideBranchExpanded(oIdx, branchId) {
      return !!this.expandedOverrideBranches[`${oIdx}-${branchId}`]
    },
    isOverrideUomExpanded(oIdx, branchId, tIdx) {
      return !!this.expandedOverrideUoms[`${oIdx}-${branchId}-${tIdx}`]
    },
    getTierById(tierId) {
      return this.pricingTiers.find(t => t.id === tierId) || null
    },
    getOverrideTierName(tierData) {
      if (typeof tierData === 'object' && tierData.name) return tierData.name
      const tierId = typeof tierData === 'object' ? tierData.price_tier : tierData
      const tier = this.getTierById(tierId)
      return tier ? tier.name : ''
    },
    getOverrideTierDescription(tierData) {
      if (typeof tierData === 'object' && tierData.description != null) return tierData.description || ''
      const tierId = typeof tierData === 'object' ? tierData.price_tier : tierData
      const tier = this.getTierById(tierId)
      return tier ? tier.description : ''
    },
    getOverrideTierPanelText(tierData) {
      const tierId = typeof tierData === 'object' ? tierData.price_tier : tierData
      const tier = this.getTierById(tierId)
      return tier ? tier.panelText : 'None included'
    },
    getOverrideTierSelfPayText(tierData) {
      const tierId = typeof tierData === 'object' ? tierData.price_tier : tierData
      const tier = this.getTierById(tierId)
      return tier ? tier.selfPayText : 'None included'
    },
    onEditOverride(index) {
      this.editingOverrideIndex = index
      this.editingOverride = JSON.parse(JSON.stringify(this.priceOverrides[index]))
      this.showOverridePricingDialog = true
    },
    onSaveOverride(overrideData) {
      // Remove saved branches from other overrides to avoid duplicates
      const savedBranches = new Set(overrideData.branches)
      for (let i = this.priceOverrides.length - 1; i >= 0; i--) {
        if (i === this.editingOverrideIndex) continue
        this.priceOverrides[i].branches = this.priceOverrides[i].branches.filter(b => !savedBranches.has(b))
        if (this.priceOverrides[i].branches.length === 0) {
          this.priceOverrides.splice(i, 1)
          if (this.editingOverrideIndex !== null && i < this.editingOverrideIndex) {
            this.editingOverrideIndex--
          }
        }
      }
      overrideData.priceTiers = overrideData.priceTiers.map(t => {
        const tier = this.pricingTiers.find(pt => pt.id === t.price_tier)
        return { ...t, name: tier?.name || '', description: tier?.description || '' }
      })
      if (this.editingOverrideIndex !== null) {
        this.$set(this.priceOverrides, this.editingOverrideIndex, overrideData)
      } else {
        this.priceOverrides.push(overrideData)
      }
      this.onCloseOverrideDialog()
    },
    onCloseOverrideDialog() {
      this.showOverridePricingDialog = false
      this.editingOverrideIndex = null
      this.editingOverride = null
    },
    async initData(silent = false) {
      if (!silent) {
        this.isFetchingMedication = true
        this.isFetchError = false
      }
      try {
        if (this.isEditMode) {
          const [medResp, branchResp, tierResp] = await Promise.all([
            this.$axios.get(`/clinics/${this.companySlug}/hq/medications/${this.medicationId}`),
            this.$axios.get(`/clinics/${this.companySlug}/hq/medications/${this.medicationId}/branches`),
            this.$axios.get('/product_pricetiers', { params: { company: this.companyId, status: 'active', page_size: -1 } })
          ])
          this.mapPricingTiers(tierResp.data)
          this.populateForm(medResp.data, branchResp.data)
        } else {
          await this.fetchBranches()
        }
      } catch {
        if (silent) {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        } else {
          this.isFetchError = true
        }
      } finally {
        if (!silent) this.isFetchingMedication = false
      }
    },
    async fetchBranches() {
      this.isFetchingBranches = true
      try {
        const [resp, tierResp] = await Promise.all([
          this.$axios.get(`/companies/${this.company.id}/branches`, { params: { page_size: -1 } }),
          this.$axios.get('/product_pricetiers', { params: { company: this.companyId, status: 'active', page_size: -1 } })
        ])
        this.mapPricingTiers(tierResp.data)
        const results = resp.data?.results || resp.data || []
        this.branchLocations = results.map(b => ({
          id: b.id,
          companyId: b.id,
          name: b.name,
          type: b.os === 'warehouse' ? 'warehouse' : 'branch',
          onHand: 0,
          available: 0,
          committed: 0,
          reserved: 0,
          inventoryQty: 0,
          locationId: null,
          storages: (b.storages || []).map(s => ({ id: s.id, value: s.value })),
          enabled: false
        }))
      } catch {
        this.showMessage(['error', 'Failed to fetch branches'])
      } finally {
        this.isFetchingBranches = false
      }
    },
    populateForm(med, assignedBranches) {
      const product = med.product || {}
      this.medicine = {
        name: med.name || product.name || '',
        generic_name: med.generic_name || '',
        category: med.category || null,
        groups: med.groups || [],
        sku: product.sku || med.sku || null,
        uom: product.uom || null,
        dosage_value: med.dosage_value || null,
        dosage_unit: med.dosage_unit || null,
        instruction: med.instruction || null,
        precaution: med.precaution || null,
        frequency: med.frequency || null,
        duration: med.duration || null,
        indication: med.indication || null,
        default_qty: med.default_qty || null,
        stock_amount_warning: product.stock_amount_warning || null,
        stock_expiry_warning: product.stock_expiry_warning || null,
        cost: product.cost != null ? product.cost / this.$currency.multiplier : null,
        last_price: product.last_price != null ? product.last_price / this.$currency.multiplier : null,
        supplier: Number(product.supplier?.id || product.supplier) || null,
        remarks: product.description || ''
      }
      // The base UOM is the last convert_to in the conversion chain
      const conversions = product.uom_conversions || []
      this.originalUom = conversions.length > 0
        ? conversions[conversions.length - 1].convert_to
        : null
      this.originalDefaultUom = product.uom || null
      this.instructionEnabled = !!med.instruction_enabled
      this.uomConversionEnabled = !!product.uom_conversion_enabled
      this.uomConversions = product.uom_conversions || []
      this.originalUomConversionEnabled = !!product.uom_conversion_enabled
      this.originalUomConversions = JSON.parse(JSON.stringify(product.uom_conversions || []))
      this.productId = product.id

      // Refresh UOM prices after conversions are populated
      this.refreshTierUomPrices()

      // Map pricing tiers from saved data
      const priceTiers = med.price_tiers || med.hq_price_tiers || []
      const multiplier = Number(this.$currency.multiplier)
      const exponent = this.$currency.exponent
      this.pricingTiers.forEach(tier => {
        const match = priceTiers.find(pt => (pt.price_tier?.id || pt.price_tier) === tier.id)
        if (match) {
          tier.price = match.price ? (match.price / multiplier).toFixed(exponent) : null
          if (match.price_per_uom && match.price_per_uom.length > 0) {
            match.price_per_uom.forEach(savedUom => {
              const uomEntry = tier.price_per_uom.find(p => p.uom === savedUom.uom)
              if (uomEntry) uomEntry.price = (savedUom.price / multiplier).toFixed(exponent)
            })
          }
        }
      })

      // Map branch-level price tier overrides from medication branches
      const branches = med.branches || []
      this.priceOverrides = branches
        .filter(b => b.price_tiers && b.price_tiers.length > 0)
        .map(b => ({
          branches: [b.company?.id || b.id],
          priceTiers: b.price_tiers.map(pt => ({
            price_tier: pt.price_tier?.id || pt.price_tier,
            price: pt.price ? (pt.price / multiplier).toFixed(exponent) : null,
            updated_by_branch: pt.updated_by_branch || false,
            price_per_uom: (pt.price_per_uom || []).map(p => ({ uom: p.uom, price: p.price ? (p.price / multiplier).toFixed(exponent) : null, updated_by_branch: p.updated_by_branch || false })),
            name: pt.price_tier?.name || '',
            description: pt.price_tier?.description || '',
          }))
        }))

      // Build branch locations from medication branches response
      const branchResults = assignedBranches?.results || assignedBranches || []
      const assignedIds = []
      this.branchLocations = branchResults.map(b => {
        const product = b.product
        const isAssigned = !!product && !product.deleted
        if (isAssigned) assignedIds.push(b.id)
        return {
          id: b.id,
          companyId: b.id,
          name: b.name,
          type: b.os === 'warehouse' ? 'warehouse' : 'branch',
          onHand: isAssigned ? (product.inventory_qty || 0) : 0,
          available: isAssigned ? (product.available_qty || 0) : 0,
          committed: isAssigned ? (product.committed_qty || 0) : 0,
          reserved: isAssigned ? (product.reserved_qty || 0) : 0,
          inventoryQty: 0,
          locationId: isAssigned && product.location ? product.location.id : null,
          storages: (b.storages || []).map(s => ({ id: s.id, value: s.value })),
          enabled: isAssigned
        }
      })
      this.originalBranchCompanyIds = assignedIds
      this.locationMeta = assignedBranches?.meta || null
    },
    buildPayload() {
      const enabledBranches = this.branchLocations.filter(l => l.enabled)
      const payload = {
        product: {
          name: this.medicine.name,
          description: this.medicine.remarks || null,
          sku: this.medicine.sku || null,
          uom: this.medicine.uom,
          uom_conversion_enabled: this.uomConversionEnabled,
          stock_amount_warning: this.medicine.stock_amount_warning || null,
          stock_expiry_warning: this.medicine.stock_expiry_warning || null,
          supplier: this.medicine.supplier || null
        },
        generic_name: this.medicine.generic_name || null,
        category: this.medicine.category,
        groups: this.medicine.groups ? this.medicine.groups.map(m => m.id) : [],
        instruction_enabled: this.instructionEnabled,
        dosage_value: this.medicine.dosage_value || null,
        dosage_unit: this.medicine.dosage_unit || null,
        instruction: this.medicine.instruction || null,
        precaution: this.medicine.precaution || null,
        frequency: this.medicine.frequency || null,
        duration: this.medicine.duration || null,
        indication: this.medicine.indication || null,
        hq_price_tiers: this.pricingTiers.filter(t => this.tierHasPrice(t)).map(t => ({
          price_tier: t.id,
          price: this.hasActiveUomConversions ? 0 : Math.round(Number(t.price) * Number(this.$currency.multiplier)),
          price_per_uom: t.price_per_uom.filter(p => p.price !== null && p.price !== '').map(p => ({ uom: p.uom, price: Math.round(Number(p.price) * Number(this.$currency.multiplier)) }))
        }))
      }
      if (this.medicine.default_qty) {
        payload.default_qty = this.medicine.default_qty
      }
      if (this.uomConversionEnabled) {
        payload.product.uom_conversions = this.uomConversions.map(c => ({ id: c.id, uom: c.uom, convert_to: c.convert_to, multiplier: c.multiplier }))
      }
      const getOverridePriceTiers = (companyId) => {
        const override = this.priceOverrides.find(o => o.branches.includes(companyId))
        if (!override) return undefined
        return override.priceTiers.map(pt => ({
          price_tier: pt.price_tier,
          price: this.hasActiveUomConversions ? 0 : Math.round(Number(pt.price || 0) * Number(this.$currency.multiplier)),
          price_per_uom: (pt.price_per_uom || []).filter(p => p.price !== null && p.price !== '').map(p => ({ uom: p.uom, price: Math.round(Number(p.price) * Number(this.$currency.multiplier)) }))
        }))
      }
      if (this.isEditMode) {
        // Edit mode: branches use { company, deleted } format, no inventory block
        const allBranchIds = new Set([...this.originalBranchCompanyIds, ...enabledBranches.map(l => l.companyId)])
        payload.branches = [...allBranchIds].map(companyId => {
          const isEnabled = enabledBranches.some(l => l.companyId === companyId)
          const branch = enabledBranches.find(l => l.companyId === companyId)
          const obj = { company: companyId, deleted: !isEnabled, location: branch ? branch.locationId : null }
          const priceTiers = getOverridePriceTiers(companyId)
          if (priceTiers) obj.price_tiers = priceTiers
          return obj
        })
      } else {
        // Add mode: include inventory block and branch inventory_qty
        const totalChange = enabledBranches.reduce((sum, l) => sum + (Number(l.inventoryQty) || 0), 0)
        payload.inventory = {
          change: totalChange,
          cost: Math.round(Number(this.stock.cost || 0) * Number(this.$currency.multiplier)),
          batch_no: this.stock.batch_no || null,
          expiry_date: this.selectedExpiryDate || null
        }
        payload.branches = enabledBranches.map(loc => {
          const obj = { company: loc.companyId, inventory_qty: Number(loc.inventoryQty) || 0, location: loc.locationId }
          const priceTiers = getOverridePriceTiers(loc.companyId)
          if (priceTiers) obj.price_tiers = priceTiers
          return obj
        })
      }
      return payload
    },
    async onSaveData() {
      if (!this.$refs.form.validate()) {
        this.showMessage(['error', 'Please fill in all required fields'])
        return
      }
      if (this.uomConversionEnabled && this.uomAllowedValues && !this.uomAllowedValues.includes(this.medicine.uom)) {
        this.uomError = true
        this.showMessage(['error', 'Default UOM must be one of the values from the UOM conversion list'])
        return
      }
      this.uomError = false
      // Show enable-conversion confirmation BEFORE cmsOsV2 + package-archive checks;
      // user must confirm auto-conversion of existing stock to the new base unit.
      const enableFlow = this.needsEnableUomConversionFlow
      if (enableFlow) {
        this.enableUomConversionData = enableFlow
        this.showEnableUomConversionDialog = true
        return
      }
      if (this.uomConversionEnabled) {
        const cmsOsV2 = await this.$store.dispatch('clinic/fetchCmsOsV2', this.$axios)
        if (!cmsOsV2) {
          this.showCmsOsV2ConsentDialog = true
          return
        }
      }
      this.checkPackagesToArchive()
    },
    onCmsOsV2Consented() {
      this.checkPackagesToArchive()
    },
    async onEnableUomConversionConfirmed() {
      // PATCH already persisted the base_uom_conversion; sync original* state so downstream
      // change-detection (hasUomConversionChanged) sees no further change in this save flow.
      this.showEnableUomConversionDialog = false
      this.originalUomConversionEnabled = true
      this.originalUom = this.medicine.uom
      this.originalDefaultUom = this.medicine.uom
      this.originalUomConversions = JSON.parse(JSON.stringify(this.uomConversions))
      this.enableUomConversionData = null
      if (this.uomConversionEnabled) {
        const cmsOsV2 = await this.$store.dispatch('clinic/fetchCmsOsV2', this.$axios)
        if (!cmsOsV2) {
          this.showCmsOsV2ConsentDialog = true
          return
        }
      }
      this.checkPackagesToArchive()
    },
    hasUomConversionChanged() {
      if (!this.isEditMode) return { changed: false }
      const collectUomNames = (conversions) => {
        const names = new Set()
        conversions.forEach(c => { names.add(c.uom); names.add(c.convert_to) })
        return names
      }
      const wasEnabled = this.originalUomConversionEnabled
      const isEnabled = this.uomConversionEnabled
      // Case 1: toggled ON (was off → now on)
      if (!wasEnabled && isEnabled) return { changed: true, removedUoms: [] }
      // Case 2: toggled OFF (was on → now off)
      if (wasEnabled && !isEnabled) {
        const originalNames = [...collectUomNames(this.originalUomConversions)]
        return { changed: true, removedUoms: originalNames }
      }
      // Case 3: still enabled, check if names changed
      if (wasEnabled && isEnabled) {
        const originalNames = collectUomNames(this.originalUomConversions)
        const currentNames = collectUomNames(this.uomConversions)
        const removedUoms = [...originalNames].filter(name => !currentNames.has(name))
        if (removedUoms.length > 0) return { changed: true, removedUoms }
      }
      // Case 4: no relevant changes
      return { changed: false }
    },
    async checkBaseUomPackages (oldUom) {
      if (!oldUom) return
      this.isCheckingPackages = true
      try {
        const resp = await this.$axios.get(`/clinics/${this.companySlug}/hq/packages`, {
          params: { status: 'published', item_uoms: `${this.productId},${oldUom}`, fields: 'id,product,treatments' }
        })
        const packages = resp.data?.results || resp.data || []
        if (packages.length > 0) {
          this.archiveFlowSource = 'base-uom-change'
          this.packagesToArchive = packages
          this.showPackageArchiveDialog = true
        } else {
          this.showBaseUomChangeDialog = true
        }
      } catch {
        this.showMessage(['error', 'Unable to check affected packages. Please try again.'])
      } finally {
        this.isCheckingPackages = false
      }
    },
    async checkPackagesToArchive() {
      const result = this.hasUomConversionChanged()
      if (!result.changed) {
        this.proceedWithSave()
        return
      }
      this.isCheckingPackages = true
      try {
        const itemUoms = result.removedUoms.length > 0
          ? `${this.productId},${result.removedUoms.join(',')}`
          : `${this.productId}`
        const resp = await this.$axios.get(`/clinics/${this.companySlug}/hq/packages`, {
          params: { status: 'published', item_uoms: itemUoms, fields: 'id,product,treatments' }
        })
        const packages = resp.data?.results || resp.data || []
        if (packages.length > 0) {
          this.packagesToArchive = packages
          this.showPackageArchiveDialog = true
        } else {
          this.proceedWithSave()
        }
      } catch {
        this.showMessage(['error', 'Unable to check affected packages. Please try again.'])
      } finally {
        this.isCheckingPackages = false
      }
    },
    async onConfirmArchivePackages() {
      this.isArchivingPackages = true
      try {
        const ids = this.packagesToArchive.map(p => p.id)
        await this.$axios.post(`/clinics/${this.companySlug}/hq/packages/bulk_archive`, { ids })
        this.showPackageArchiveDialog = false
        if (this.archiveFlowSource === 'base-uom-change') {
          this.archiveFlowSource = null
          this.showBaseUomChangeDialog = true
        } else {
          this.proceedWithSave()
        }
      } catch {
        this.showMessage(['error', 'Failed to archive packages. Please try again.'])
      } finally {
        this.isArchivingPackages = false
      }
    },
    onCloseArchiveDialog() {
      this.showPackageArchiveDialog = false
      this.packagesToArchive = []
      this.archiveFlowSource = null
      this.pendingBaseUomData = null
    },
    async proceedWithSave() {
      this.isLoading = true
      try {
        const data = this.buildPayload()
        let resp
        if (this.isEditMode) {
          resp = await this.$axios.patch(`/clinics/${this.companySlug}/hq/medications/${this.medicationId}`, data)
          this.showMessage(['success', 'Medication updated successfully'])
          this.$emit('updated', resp.data)
        } else {
          resp = await this.$axios.post(`/clinics/${this.companySlug}/hq/medications`, data)
          this.showMessage(['success', 'Medication created successfully'])
          this.$emit('created', resp.data)
        }
        this.onCloseDialog()
      } catch {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isLoading = false
      }
    },
    onCloseDialog() {
      this.$emit('closed')
    },
    onUpdateCost() {
      const enabledBranches = this.branchLocations.filter(l => l.enabled)
      const stockCount = enabledBranches.reduce((sum, l) => sum + (Number(l.inventoryQty) || 0), 0)
      const cost = this.stock.cost || 0
      const costPerItem = Number(cost) / (Number(stockCount) || 1)
      this.$set(this.medicine, 'cost', costPerItem.toFixed(this.$currency.exponent))
    },
    onPickDate() {
      this.expiryDateField = this.$options.filters.humanizeDate(this.selectedExpiryDate)
      this.showExpiredPicker = false
    },
    onAddUomConversion() {
      this.showUomConversionDialog = true
    },
    onSaveUomConversions(conversions) {
      this.uomConversions = conversions
    },
    async onBaseUomChangeRequested ({ conversions, newBaseUom }) {
      this.pendingBaseUomData = { conversions, newBaseUom }
      const oldUom = this.medicine.uom
      await this.checkBaseUomPackages(oldUom)
    },
    async onBaseUomSaved () {
      this.showBaseUomChangeDialog = false
      this.showUomConversionDialog = false
      this.uomConversions = this.pendingBaseUomData.conversions
      await this.initData(true)
      this.$emit('updated')
      this.pendingBaseUomData = null
    },
    onSupplierDeleted() {
      this.medicine.supplier = null
    },
    onToggleLocation(item, newValue) {
      if (!newValue && this.isEditMode && this.originalBranchCompanyIds.includes(item.companyId)) {
        this.$nextTick(() => { item.enabled = true })
        if (item.onHand > 0) {
          this.showStockWarningDialog = true
        } else {
          this.pendingUnassignItem = item
          this.showUnassignDialog = true
        }
      }
    },
    onConfirmUnassign() {
      if (this.pendingUnassignItem) {
        this.pendingUnassignItem.enabled = false
      }
      this.showUnassignDialog = false
      this.pendingUnassignItem = null
    },
    onOpenTierDetails(tier) {
      this.selectedTier = tier
      this.showPricingTierDetailsDialog = true
    },
    onCancelUnassign() {
      this.showUnassignDialog = false
      this.pendingUnassignItem = null
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep {
  .v-text-field--outlined {
    fieldset {
      border-color: var(--v-neutral400);
    }
  }
  .error--text {
    color: var(--v-danger500) !important;
    fieldset {
      border-color: var(--v-danger500);
    }
  }
}
.button-box {
  border-left: solid 1px var(--v-neutral400);
  border-top: solid 1px var(--v-neutral400);
  border-bottom: solid 1px var(--v-neutral400);
  border-radius: 10px 0 0 10px;
  background-color: var(--v-neutral200);
}
.summary-table ::v-deep thead th {
  padding: 0 !important;
  border: none !important;
  font-size: 0 !important;
  line-height: 0 !important;
  overflow: hidden !important;
}
.hidden {
  visibility: hidden !important;
}
.override-branch-expanded td {
  background-color: var(--v-neutral200);
}
</style>
